<template>
  <div>
    <v-card>
      <v-alert type="info" dense text border="left" class="ma-4 mb-0">
        {{ $t('coinHistory.availableFrom', { date: availableFromDate }) }}
      </v-alert>
      <v-row class="px-4 pt-4 mb-4" align="center">
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="selectedUserId"
            :items="autocompleteItems"
            :loading="loadingUsers"
            item-text="userInfo"
            item-value="id"
            :label="$t('purchase.searchUser')"
            prepend-inner-icon="mdi-magnify"
            :search-input.sync="search"
            no-filter
            solo
            hide-details
            clearable>
            <template #item="{ item }">
              <v-list-item-content>
                <v-list-item-title v-text="item.displayName" />
                <v-list-item-subtitle v-if="item.cardId">
                  {{ $t('entities.users.attributes.cardId') }}:
                  {{ item.cardId }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.sort"
            :items="sortOptions"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="isLoadingHistory"
            hide-details="auto"
            item-text="text"
            item-value="value"
            solo>
            <template v-slot:label>
              {{ $t('miscellaneous.sortBy') }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="pagination.size"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :disabled="!rowsPerPageItems.length || isLoadingHistory"
            hide-details="auto"
            solo>
            <template v-slot:label>
              {{ $t('components.rowsPerPage.entries') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-alert v-if="!selectedUserId" type="info" dense text class="mx-4">
        {{ $t('coinHistory.selectUserReminder') }}
      </v-alert>

      <template v-else>
        <v-data-table
          :headers="headers"
          :items="history"
          :items-per-page="pagination.size"
          :loading="isLoadingHistory || loadingDetails"
          :loading-text="$t('components.noData.fetchingEntries')"
          disable-sort
          hide-default-footer>
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('coinHistory.title') }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical />
              <small class="mr-2">{{ pagination.count }}</small>
              <v-btn
                depressed
                x-small
                dark
                fab
                color="primary"
                :loading="isLoadingHistory"
                :disabled="isLoadingHistory"
                @click="refresh">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
          </template>
          <template v-slot:item.user_id="{ item }">
            <a
              class="link primary--text"
              @click.stop="openUserModal"
              v-text="selectedUsernameDisplay || item.user_id" />
          </template>
          <template v-slot:item.arena_id="{ item }">
            {{ arenaName(item.arena_id) }}
          </template>
          <template v-slot:item.cart_id="{ item }">
            <a
              v-if="item.cart_id && (item.invoice_id || item.transaction_id)"
              class="link primary--text"
              :title="item.cart_id"
              @click.stop="handleV2CartClick(item)">
              {{ short(item.cart_id) }}
            </a>
            <span v-else :title="item.cart_id || ''">
              {{ short(item.cart_id) }}
            </span>
          </template>
          <template v-slot:item.transaction_id="{ item }">
            <a
              v-if="item.transaction_id"
              class="link primary--text"
              :title="item.transaction_id"
              @click.stop="openV2Transaction(item)">
              {{ short(item.transaction_id) }}
            </a>
          </template>
          <template v-slot:item.invoice_id="{ item }">
            <a
              v-if="item.invoice_id"
              class="link primary--text"
              :title="item.invoice_id"
              @click.stop="openV2Invoice(item)">
              {{ short(item.invoice_id) }}
            </a>
          </template>
          <template v-slot:item.diff_without_coin_gain_bonus="{ item }">
            <span
              :class="diffClass(item.diff_without_coin_gain_bonus)"
              class="font-weight-bold">
              {{ item.diff_without_coin_gain_bonus }}
            </span>
          </template>
          <template v-slot:item.diff_with_coin_gain_bonus="{ item }">
            <span
              v-if="item.diff_with_coin_gain_bonus > 0"
              :class="diffClass(item.diff_with_coin_gain_bonus)"
              class="font-weight-bold">
              {{ item.diff_with_coin_gain_bonus }}
            </span>
            <span v-else>—</span>
          </template>
          <template v-slot:item.session_ids="{ item }">
            <span
              v-for="(sessionId, index) in item.session_ids || []"
              :key="index">
              <a
                class="link primary--text"
                :title="sessionId"
                @click.stop="openSession(item, sessionId)">
                {{ short(sessionId) }}</a
              ><span v-if="index < item.session_ids.length - 1">, </span>
            </span>
          </template>
          <template v-slot:item.coins_item_id="{ item }">
            <a
              v-if="item.coins_item_id"
              class="link primary--text"
              @click.stop="openItem(item, item.coins_item_id)">
              {{ item.coins_item_id }}
            </a>
          </template>
          <template v-slot:item.bought_item_id="{ item }">
            <a
              v-if="item.bought_item_id"
              class="link primary--text"
              @click.stop="openItem(item, item.bought_item_id)">
              {{ item.bought_item_id }}
            </a>
          </template>
          <template v-slot:item.shop_v1_cart_id="{ item }">
            <a
              v-if="
                item.shop_v1_cart_id &&
                (item.shop_v1_invoice_id || item.shop_v1_transaction_id)
              "
              class="link primary--text"
              @click.stop="handleV1CartClick(item)">
              {{ item.shop_v1_cart_id }}
            </a>
            <span v-else>{{ item.shop_v1_cart_id ?? '' }}</span>
          </template>
          <template v-slot:item.shop_v1_invoice_id="{ item }">
            <a
              v-if="item.shop_v1_invoice_id"
              class="link primary--text"
              @click.stop="goToV1Invoices">
              {{ item.shop_v1_invoice_id }}
            </a>
          </template>
          <template v-slot:item.shop_v1_transaction_id="{ item }">
            <a
              v-if="item.shop_v1_transaction_id"
              class="link primary--text"
              @click.stop="goToV1Transactions">
              {{ item.shop_v1_transaction_id }}
            </a>
          </template>
          <template v-slot:item.daily_interest_payment_skill="{ item }">
            <v-icon
              v-if="item.daily_interest_payment_skill"
              color="green"
              small>
              mdi-check
            </v-icon>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateValue(item.created_at) }}
          </template>
          <template v-slot:item.last_charged_at="{ item }">
            {{ hasSessions(item) ? formatDateValue(item.last_charged_at) : '' }}
          </template>
          <template v-slot:no-data>
            <div class="py-6 text-center grey--text">
              {{ $t('components.noData.noEntriesFound') }}
            </div>
          </template>
        </v-data-table>
        <v-layout
          v-if="pagination.count"
          class="text-center mt-3 pb-4"
          justify-center>
          <v-flex>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.last_page"
              :total-visible="$vuetify.breakpoint.mdAndUp ? 10 : 5"
              :disabled="isLoadingHistory"
              prev-icon="mdi-menu-left"
              next-icon="mdi-menu-right" />
            <h5 class="card-title font-weight-light mt-2">
              {{
                `${pagination.from} - ${pagination.to} ${$t(
                  'components.pagination.of',
                )} ${pagination.count}`
              }}
            </h5>
          </v-flex>
        </v-layout>
      </template>
    </v-card>

    <ViewDialog
      :value="invoiceDialogOpen"
      :invoice="invoiceToView"
      :arena-id="invoiceDialogArenaId"
      :arena-name="arenaName(invoiceDialogArenaId)"
      :currency-symbol="currencyByArena[invoiceDialogArenaId] || ''"
      :payment-methods-by-id="paymentMethodsById"
      :users-by-id="usersById"
      @input="invoiceDialogOpen = $event" />

    <CoinDetailsDialog
      :value="detailsDialogOpen"
      :invoice="detailsInvoice"
      :transaction="detailsTransaction"
      :arena-name="arenaName(detailsArenaId)"
      :currency-symbol="currencyByArena[detailsArenaId] || ''"
      :payment-methods-by-id="paymentMethodsById"
      :users-by-id="usersById"
      @input="detailsDialogOpen = $event" />

    <CoinItemDialog
      :value="itemDialogOpen"
      :item="itemToView"
      @input="itemDialogOpen = $event" />

    <CoinSessionDialog
      :value="sessionDialogOpen"
      :session="sessionToView"
      @input="sessionDialogOpen = $event" />

    <div v-if="selectedUserId" style="display: none">
      <UserActions
        ref="userActions"
        :entity="userActionsEntity"
        :viewButton="false"
        :editButton="false"
        :shopButton="false"
        :awardButton="false"
        :simulateSessionButton="false"
        :sessionButton="false"
        :deleteButton="false" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import User from '@/models/users/User'
import Arena from '@/models/setup/Arena'
import InvoiceV2 from '@/models/shop/InvoiceV2'
import TransactionV2 from '@/models/shop/TransactionV2'
import PaymentMethod from '@/models/shop/PaymentMethod'
import Currency from '@/models/shop/Currency'
import formatDate from '@/mixins/format-date'
import ViewDialog from '@/views/shop-v2/invoices/ViewDialog.vue'
import CoinDetailsDialog from './CoinDetailsDialog.vue'
import CoinItemDialog from './CoinItemDialog.vue'
import CoinSessionDialog from './CoinSessionDialog.vue'
import Session from '@/models/main/Session'
import UserActions from '@/components/UserActions.vue'

const resetPagination = () => ({
  size: 10,
  from: 0,
  to: 0,
  page: 1,
  last_page: 0,
  count: 0,
})

export default {
  name: 'CoinHistory',
  components: {
    ViewDialog,
    CoinDetailsDialog,
    CoinItemDialog,
    CoinSessionDialog,
    UserActions,
  },
  mixins: [formatDate],
  data() {
    return {
      filters: {
        sort: '-created_at',
      },
      availableFromDate: '2026-06-24',
      selectedUserId: null,
      selectedUser: null,
      users: [],
      loadingUsers: false,
      search: '',
      searchDebounce: null,
      history: [],
      isLoadingHistory: false,
      pagination: resetPagination(),
      // lookups / caches
      arenaNamesById: {},
      currencyByArena: {},
      paymentMethodsById: {},
      paymentMethodsLoaded: false,
      usersById: {},
      // details dialogs
      loadingDetails: false,
      invoiceDialogOpen: false,
      invoiceToView: null,
      invoiceDialogArenaId: null,
      detailsDialogOpen: false,
      detailsInvoice: null,
      detailsTransaction: null,
      detailsArenaId: null,
      itemDialogOpen: false,
      itemToView: null,
      sessionDialogOpen: false,
      sessionToView: null,
    }
  },
  computed: {
    rowsPerPageItems() {
      return [5, 10, 25, 50]
    },
    sortOptions() {
      return [
        {
          text: this.$t('entities.products.filters.newest'),
          value: '-created_at',
        },
        {
          text: this.$t('entities.products.filters.oldest'),
          value: 'created_at',
        },
      ]
    },
    headers() {
      return [
        { text: 'Username', value: 'user_id' },
        { text: 'Coins Before', value: 'coins_before' },
        {
          text: 'Diff (without bonus)',
          value: 'diff_without_coin_gain_bonus',
        },
        {
          text: 'Diff with coin gain bonus',
          value: 'diff_with_coin_gain_bonus',
        },
        { text: 'Coins After', value: 'coins_after' },
        { text: 'Arena', value: 'arena_id' },
        { text: 'Cart ID', value: 'cart_id' },
        { text: 'Transaction ID', value: 'transaction_id' },
        { text: 'Invoice ID', value: 'invoice_id' },
        { text: 'Session IDs', value: 'session_ids' },
        { text: 'Coins Item ID', value: 'coins_item_id' },
        { text: 'Bought Item ID', value: 'bought_item_id' },
        { text: 'Shop V1 Cart ID', value: 'shop_v1_cart_id' },
        { text: 'Shop V1 Invoice ID', value: 'shop_v1_invoice_id' },
        { text: 'Shop V1 Transaction ID', value: 'shop_v1_transaction_id' },
        {
          text: 'Daily Interest Payment Skill',
          value: 'daily_interest_payment_skill',
        },
        { text: 'Created At', value: 'created_at' },
        { text: 'Last Charged At', value: 'last_charged_at' },
      ]
    },
    autocompleteItems() {
      const items = this.users.map((user) => this.toAutocompleteItem(user))
      if (
        this.selectedUser &&
        !items.some((item) => item.id === this.selectedUser.id)
      ) {
        items.unshift(this.toAutocompleteItem(this.selectedUser))
      }
      return items
    },
    selectedUsernameDisplay() {
      const user = this.selectedUser
      if (!user) return ''
      return user.identity?.name
        ? `${user.identity.name} (${user.username})`
        : user.username
    },
    userActionsEntity() {
      // Pass only the id so UserActions fetches and normalizes the user
      // (identity/phone/email defaults) via setLocalEntity. Passing the raw
      // selectedUser (which has a username but no nested objects) makes
      // openViewDialog skip that normalization and the dialog fails to render.
      return { id: this.selectedUserId }
    },
  },
  created() {
    this.prefillUserFromQuery()
  },
  methods: {
    ...mapActions(['showSnackbar']),
    async prefillUserFromQuery() {
      const userId = this.$route.query.user_id
      if (!userId) return
      try {
        const response = await User.api.find(userId)
        const user = response.data
        if (!user) return
        this.users = [user]
        this.selectedUser = user
        this.selectedUserId = user.id
      } catch (error) {
        console.warn('Failed to prefill user from query.', error)
      }
    },
    syncUserQuery(userId) {
      // Keep the URL in sync with the selection so a refresh reflects the
      // current state (e.g. clearing the user removes the query param).
      const current = this.$route.query.user_id ?? null
      const next = userId ? String(userId) : null
      if (current === next) return
      const query = { ...this.$route.query }
      if (next) {
        query.user_id = next
      } else {
        delete query.user_id
      }
      this.$router
        .replace({
          name: this.$route.name,
          params: { arena: this.$route.params.arena },
          query,
        })
        .catch(() => undefined)
    },
    toAutocompleteItem(user) {
      const displayName = user.identity?.name
        ? `${user.identity.name} (${user.username})`
        : user.username
      return {
        id: user.id,
        displayName,
        userInfo: displayName,
        cardId: user.card_id || null,
      }
    },
    short(value) {
      if (!value) return ''
      return String(value).slice(0, 8)
    },
    hasSessions(item) {
      return Array.isArray(item.session_ids) && item.session_ids.length > 0
    },
    diffClass(value) {
      if (value > 0) return 'green--text'
      if (value < 0) return 'red--text'
      return ''
    },
    formatDateValue(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return this.formatDate(parsed, true, true)
    },
    arenaName(arenaId) {
      if (!arenaId) return ''
      return this.arenaNamesById[arenaId] ?? String(arenaId)
    },
    openUserModal() {
      this.$nextTick(() => {
        this.$refs.userActions?.openViewDialog?.()
      })
    },
    async fetchUsers(query = '') {
      try {
        this.loadingUsers = true
        const params = this.getUserRequestParams(query)
        const response = await User.api.get(params)
        this.users = response.data?.content || []
      } catch (error) {
        console.warn('Searching for users failed.', error)
        this.users = []
      } finally {
        this.loadingUsers = false
      }
    },
    getUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const searchParams = this.getSearchParams(query)
      return `size=10&page=1&${searchParams}`
    },
    getSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    setPaginationTotals(metadata = null, dataLength = 0) {
      const currentPage = this.pagination.page || 1
      const size = this.pagination.size || 10
      const count = metadata?.count ?? dataLength ?? 0
      const lastPage = size ? Math.ceil((count || 0) / size) : 0
      const from = count ? (currentPage - 1) * size + 1 : 0
      const to = count ? Math.min(from + size - 1, count) : 0
      this.pagination.from = from
      this.pagination.to = to
      this.pagination.page = currentPage
      this.pagination.last_page = lastPage
      this.pagination.count = count
    },
    async fetchHistory() {
      if (!this.selectedUserId) {
        this.history = []
        this.pagination = resetPagination()
        return
      }
      this.isLoadingHistory = true
      try {
        const { page, size } = this.pagination
        const params = {
          page,
          size,
          sort: this.filters.sort,
        }
        const response = await User.api.getWalletHistory(
          this.selectedUserId,
          params,
        )
        this.history = response.data?.data || []
        const metadata = response.data?.metadata || {}
        this.setPaginationTotals(metadata, this.history.length)
        this.fetchArenaNamesForHistory()
      } catch (error) {
        console.error('Failed to fetch coin history.', error)
        this.history = []
        this.pagination = resetPagination()
      } finally {
        this.isLoadingHistory = false
      }
    },
    fetchArenaNamesForHistory() {
      const arenaIds = [
        ...new Set(this.history.map((row) => row.arena_id).filter(Boolean)),
      ]
      arenaIds.forEach((id) => this.ensureArenaName(id))
    },
    async ensureArenaName(arenaId) {
      if (!arenaId || this.arenaNamesById[arenaId] !== undefined) return
      try {
        const response = await Arena.api.read(arenaId, '')
        const name = response.data?.name || String(arenaId)
        this.arenaNamesById = { ...this.arenaNamesById, [arenaId]: name }
      } catch (error) {
        console.warn('Failed to fetch arena name.', error)
        this.arenaNamesById = {
          ...this.arenaNamesById,
          [arenaId]: String(arenaId),
        }
      }
    },
    async ensureCurrency(arenaId) {
      if (!arenaId || this.currencyByArena[arenaId] !== undefined) return
      try {
        const response = await Currency.api.getArenaCurrencies(arenaId)
        const currency = (response.data?.data ?? [])[0]
        const symbol = currency ? currency.symbol || currency.code : ''
        this.currencyByArena = { ...this.currencyByArena, [arenaId]: symbol }
      } catch (error) {
        console.warn('Failed to fetch currency.', error)
        this.currencyByArena = { ...this.currencyByArena, [arenaId]: '' }
      }
    },
    async ensurePaymentMethods() {
      if (this.paymentMethodsLoaded) return
      try {
        const response = await PaymentMethod.api.list()
        const payload = response?.data?.data ?? response?.data ?? []
        const list = Array.isArray(payload) ? payload : []
        this.paymentMethodsById = list.reduce((acc, method) => {
          acc[method.id] = method
          return acc
        }, {})
        this.paymentMethodsLoaded = true
      } catch (error) {
        console.warn('Failed to fetch payment methods.', error)
      }
    },
    async ensureUsers(ids = []) {
      const missing = [
        ...new Set(ids.filter((id) => id && !this.usersById[id])),
      ]
      if (!missing.length) return
      try {
        const response = await User.api.findByIds(missing)
        const users = response.data || []
        const additions = users.reduce((acc, user) => {
          acc[user.id] = user
          return acc
        }, {})
        this.usersById = { ...this.usersById, ...additions }
      } catch (error) {
        console.warn('Failed to fetch users for details.', error)
      }
    },
    handleV2CartClick(item) {
      if (item.invoice_id && item.transaction_id) {
        this.openV2Combined(item)
      } else if (item.invoice_id) {
        this.openV2Invoice(item)
      } else if (item.transaction_id) {
        this.openV2Transaction(item)
      }
    },
    async openV2Invoice(item) {
      const arenaId = item.arena_id
      if (!arenaId || !item.invoice_id) return
      this.loadingDetails = true
      try {
        await Promise.all([
          this.ensurePaymentMethods(),
          this.ensureCurrency(arenaId),
          this.ensureArenaName(arenaId),
        ])
        const response = await InvoiceV2.api.getInvoice(
          String(arenaId),
          item.invoice_id,
        )
        const invoice = response.data
        await this.ensureUsers([invoice?.buyer_user_id])
        this.invoiceToView = invoice
        this.invoiceDialogArenaId = arenaId
        this.invoiceDialogOpen = true
      } catch (error) {
        console.error('Failed to load invoice.', error)
        this.showSnackbar({
          color: 'error',
          message: this.$t('coinHistory.detailsError'),
        })
      } finally {
        this.loadingDetails = false
      }
    },
    async openV2Transaction(item) {
      const arenaId = item.arena_id
      if (!arenaId || !item.transaction_id) return
      this.loadingDetails = true
      try {
        await this.ensureArenaName(arenaId)
        const response = await TransactionV2.api.get(
          String(arenaId),
          item.transaction_id,
        )
        const transaction = response.data
        await this.ensureUsers([
          transaction?.seller_user_id,
          transaction?.buyer_user_id,
        ])
        this.detailsInvoice = null
        this.detailsTransaction = transaction
        this.detailsArenaId = arenaId
        this.detailsDialogOpen = true
      } catch (error) {
        console.error('Failed to load transaction.', error)
        this.showSnackbar({
          color: 'error',
          message: this.$t('coinHistory.detailsError'),
        })
      } finally {
        this.loadingDetails = false
      }
    },
    async openV2Combined(item) {
      const arenaId = item.arena_id
      if (!arenaId || !item.invoice_id || !item.transaction_id) return
      this.loadingDetails = true
      try {
        await Promise.all([
          this.ensurePaymentMethods(),
          this.ensureCurrency(arenaId),
          this.ensureArenaName(arenaId),
        ])
        const [invoiceResponse, transactionResponse] = await Promise.all([
          InvoiceV2.api.getInvoice(String(arenaId), item.invoice_id),
          TransactionV2.api.get(String(arenaId), item.transaction_id),
        ])
        const invoice = invoiceResponse.data
        const transaction = transactionResponse.data
        await this.ensureUsers([
          invoice?.buyer_user_id,
          transaction?.seller_user_id,
          transaction?.buyer_user_id,
        ])
        this.detailsInvoice = invoice
        this.detailsTransaction = transaction
        this.detailsArenaId = arenaId
        this.detailsDialogOpen = true
      } catch (error) {
        console.error('Failed to load details.', error)
        this.showSnackbar({
          color: 'error',
          message: this.$t('coinHistory.detailsError'),
        })
      } finally {
        this.loadingDetails = false
      }
    },
    async openItem(row, itemId) {
      const userId = row.user_id || this.selectedUserId
      if (!userId || !itemId) return
      this.loadingDetails = true
      try {
        const response = await User.api.getUserItem(userId, itemId)
        this.itemToView = response.data
        this.itemDialogOpen = true
      } catch (error) {
        console.error('Failed to load item.', error)
        this.showSnackbar({
          color: 'error',
          message: this.$t('coinHistory.detailsError'),
        })
      } finally {
        this.loadingDetails = false
      }
    },
    async openSession(row, sessionId) {
      const arenaId = row.arena_id
      const userId = row.user_id || this.selectedUserId
      if (!arenaId || !userId || !sessionId) return
      this.loadingDetails = true
      try {
        const response = await Session.api.getUserSession(
          arenaId,
          userId,
          sessionId,
        )
        this.sessionToView = response.data
        this.sessionDialogOpen = true
      } catch (error) {
        console.error('Failed to load session.', error)
        this.showSnackbar({
          color: 'error',
          message: this.$t('coinHistory.detailsError'),
        })
      } finally {
        this.loadingDetails = false
      }
    },
    handleV1CartClick(item) {
      if (item.shop_v1_invoice_id) {
        this.goToV1Invoices()
      } else if (item.shop_v1_transaction_id) {
        this.goToV1Transactions()
      }
    },
    goToV1Invoices() {
      this.$router
        .push({ name: 'navigationDrawer.shop.invoices' })
        .catch(() => undefined)
    },
    goToV1Transactions() {
      this.$router
        .push({ name: 'entities.invoices.transactions' })
        .catch(() => undefined)
    },
    refresh() {
      this.pagination.page = 1
      this.fetchHistory()
    },
    onFirstPage() {
      return this.pagination.page === 1
    },
    setPageToOne() {
      this.pagination.page = 1
    },
  },
  watch: {
    search(newVal) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      if (newVal && newVal.trim()) {
        this.searchDebounce = setTimeout(() => {
          this.fetchUsers(newVal)
        }, 400)
      } else {
        this.users = []
      }
    },
    selectedUserId(newVal) {
      this.selectedUser =
        this.users.find((user) => user.id === newVal) || this.selectedUser
      this.syncUserQuery(newVal)
      this.pagination.page = 1
      this.fetchHistory()
    },
    'pagination.size'(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchHistory()
      } else {
        this.setPageToOne()
      }
    },
    'pagination.page'(newVal, oldVal) {
      if (newVal === oldVal) return
      this.fetchHistory()
    },
    'filters.sort'() {
      this.pagination.page = 1
      this.fetchHistory()
    },
  },
}
</script>

<style scoped>
.link {
  cursor: pointer;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}
</style>
